import React, { useState } from "react";
import { Phone, Mail, MapPin, Clock, MessageCircle, AlertCircle, Globe, Shield, Star } from "lucide-react";
import styles from "./Contact.module.css";

export default function Contact() {
  const [expandedFAQ, setExpandedFAQ] = useState(null);



  const toggleFAQ = (index) => {
    setExpandedFAQ(expandedFAQ === index ? null : index);
  };

  const contactMethods = [
    {
      icon: <Phone className={styles.contactMethodIcon} />,
      title: "Call Us",
      subtitle: "Available 24/7",
      value: "+20 110 462 5154",
      action: "tel:+201104625154",
      description: "Speak directly with our booking specialists"
    },
    {
      icon: <MessageCircle className={styles.contactMethodIcon} />,
      title: "WhatsApp",
      subtitle: "Quick Response",
      value: "+20 127 494 8282",
      action: "https://wa.me/201274948282",
      description: "Chat with us for instant quotes and booking"
    },
  ];

  const businessHours = [
    { day: "Monday - Friday", hours: "8:00 AM - 10:00 PM" },
    { day: "Saturday", hours: "9:00 AM - 9:00 PM" },
    { day: "Sunday", hours: "10:00 AM - 8:00 PM" },
    { day: "Emergency Support", hours: "24/7 Available" }
  ];

  const faqs = [
    {
      question: "How do I book a tour?",
      answer: "You can book through our website form, call us directly, or send a WhatsApp message. We'll respond with availability and pricing within 30 minutes during business hours."
    },
    {
      question: "Are all vehicles air-conditioned?",
      answer: "Yes, all our vehicles are modern, fully air-conditioned, and regularly maintained. We ensure maximum comfort for all journeys, especially important in Egypt's climate."
    },
    {
      question: "Do you offer long-distance trips?",
      answer: "Absolutely! We provide tours throughout Egypt including Cairo, Alexandria, Luxor, Aswan, Red Sea resorts, and desert destinations. Multi-day trips can be arranged with hotel accommodations."
    },
    {
      question: "What languages do your drivers speak?",
      answer: "Our drivers are multilingual, speaking Arabic, English, and additional languages including French, German, and Italian depending on the driver assigned to your tour."
    },
    {
      question: "Is payment secure?",
      answer: "Yes, we accept secure payments through multiple methods including cash, bank transfer, and major credit cards. We also offer flexible payment plans for longer tours."
    },
    {
      question: "What's included in the tour price?",
      answer: "Tour prices include professional driver, fuel, vehicle insurance, and basic refreshments. Entrance fees to attractions, meals, and accommodation are typically separate unless specified in premium packages."
    },
    {
      question: "Can I customize my itinerary?",
      answer: "Definitely! We specialize in customized tours. Tell us your interests, budget, and time frame, and we'll create a personalized itinerary just for you."
    },
    {
      question: "What's your cancellation policy?",
      answer: "Free cancellation up to 24 hours before your tour. For cancellations within 24 hours, a 50% refund applies. No-shows are non-refundable. Weather-related cancellations receive full refunds."
    }
  ];

  const features = [
    {
      icon: <Shield className={styles.featureIcon} />,
      title: "Licensed & Insured",
      description: "All drivers and vehicles are fully licensed and insured"
    },
    {
      icon: <Clock className={styles.featureIcon} />,
      title: "24/7 Support",
      description: "Round-the-clock customer support and emergency assistance"
    },
    {
      icon: <Star className={styles.featureIcon} />,
      title: "5-Star Rated",
      description: "Consistently rated 5 stars by thousands of satisfied customers"
    },
    {
      icon: <Globe className={styles.featureIcon} />,
      title: "Multilingual Service",
      description: "Communicate in your preferred language throughout your journey"
    }
  ];

  return (
    <div className={styles.container}>
      {/* Header Section */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>Contact Mariam Tours</h1>
          <p className={styles.subtitle}>
            Ready to explore Egypt? Get in touch with our friendly team to plan your perfect journey. 
            We're here to help you create unforgettable memories.
          </p>
        </div>
      </div>

      {/* Contact Methods Grid */}
      <section className={styles.contactMethods}>
        <div className={styles.methodsGrid}>
          {contactMethods.map((method, index) => (
            <a 
              key={index} 
              href={method.action}
              className={styles.methodCard}
              target={method.action.startsWith('http') ? '_blank' : '_self'}
              rel={method.action.startsWith('http') ? 'noopener noreferrer' : ''}
            >
              <div className={styles.methodIcon}>
                {method.icon}
              </div>
              <div className={styles.methodInfo}>
                <h3 className={styles.methodTitle}>{method.title}</h3>
                <p className={styles.methodSubtitle}>{method.subtitle}</p>
                <p className={styles.methodValue}>{method.value}</p>
                <p className={styles.methodDescription}>{method.description}</p>
              </div>
              <div className={styles.methodArrow}>→</div>
            </a>
          ))}
        </div>
      </section>

                     {/* Main Content Grid */}
        <div className={styles.mainContent}>
          {/* Business Hours and Why Choose Us Row */}
          <div className={styles.infoSectionsRow}>
            {/* Business Hours Section */}
            <div className={styles.infoSection}>
              <div className={styles.infoCard}>
                <h3 className={styles.infoTitle}>
                  <Clock className={styles.infoIcon} />
                  Business Hours
                </h3>
                <div className={styles.hoursList}>
                  {businessHours.map((hours, index) => (
                    <div key={index} className={styles.hoursItem}>
                      <span className={styles.day}>{hours.day}</span>
                      <span className={styles.hours}>{hours.hours}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Why Choose Us Section */}
            <div className={styles.infoSection}>
              <div className={styles.infoCard}>
                <h3 className={styles.infoTitle}>Why Choose Us?</h3>
                <div className={styles.featuresList}>
                  {features.map((feature, index) => (
                    <div key={index} className={styles.feature}>
                      {feature.icon}
                      <div>
                        <h4 className={styles.featureTitle}>{feature.title}</h4>
                        <p className={styles.featureDescription}>{feature.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Emergency Contact Section */}
          <div className={styles.infoSection}>
            <div className={styles.emergencyCard}>
              <AlertCircle className={styles.emergencyIcon} />
              <div>
                <h3 className={styles.emergencyTitle}>24/7 Emergency Support</h3>
                <p className={styles.emergencyDescription}>
                  Need immediate assistance? Our emergency hotline is available around the clock.
                </p>
                <a href="tel:+201001234567" className={styles.emergencyButton}>
                  Call Emergency Line
                </a>
              </div>
            </div>
          </div>
        </div>

      {/* FAQ Section */}
      <section className={styles.faqSection}>
        <div className={styles.faqHeader}>
          <h2 className={styles.faqTitle}>Frequently Asked Questions</h2>
          <p className={styles.faqSubtitle}>
            Find answers to common questions about our tours and services
          </p>
        </div>

        <div className={styles.faqList}>
          {faqs.map((faq, index) => (
            <div key={index} className={styles.faqItem}>
              <button
                className={styles.faqQuestion}
                onClick={() => toggleFAQ(index)}
                aria-expanded={expandedFAQ === index}
              >
                <span>{faq.question}</span>
                <span className={`${styles.faqToggle} ${expandedFAQ === index ? styles.expanded : ''}`}>
                  +
                </span>
              </button>
              {expandedFAQ === index && (
                <div className={styles.faqAnswer}>
                  <p>{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}